import { User } from 'src/constants';
import { updateClientUser } from 'src/store/clients/actions';
import { Client, UPDATE_CLIENTS_SUCCESS } from 'src/store/clients/types';
import { alertSnackbar } from 'src/store/ui/actions';
import { ensureApiError } from 'src/utils/Errors';

import { ApiMethods, appAPI } from '.';

export const clientsApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    inviteClient: build.mutation<void, { id: string }>({
      query: ({ id }) => ({
        path: `/v1/users/clients/${id}/invite`,
        method: ApiMethods.post,
        options: {},
      }),
    }),
    updateMfa: build.mutation<
      User,
      { id: string; mfaPreference: 'NOMFA' | 'SOFTWARE_TOKEN_MFA' }
    >({
      query: ({ id, mfaPreference }) => ({
        path: `/v1/users/clients/${id}/mfa`,
        method: ApiMethods.put,
        options: {
          body: { mfaPreference },
        },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            updateClientUser(
              { userId: data.id, mfaPreference: data.fields.mfaPreference },
              { showSuccessMessage: false },
            ),
          );
          dispatch(alertSnackbar({ successMessage: 'MFA has been reset.' }));
        } catch (err) {
          const { message } = ensureApiError(err);
          dispatch(alertSnackbar({ errorMessage: message }));
        }
      },
    }),
    updateNotificationsMuted: build.mutation<
      User,
      { id: string; notificationsMuted: boolean; clientName: string }
    >({
      query: ({ id, notificationsMuted }) => ({
        path: `/v1/users/clients/${id}/notifications`,
        method: ApiMethods.put,
        options: {
          body: { notificationsMuted },
        },
      }),
      onQueryStarted: async (
        { id, notificationsMuted, clientName },
        { dispatch, queryFulfilled },
      ) => {
        const updateStore = (muted: boolean) =>
          dispatch({
            type: UPDATE_CLIENTS_SUCCESS,
            payload: [
              {
                id,
                fields: { notificationsMuted: muted },
              } as Partial<Client>,
            ],
          });

        // Optimistic update — only update Redux store, no extra API call
        updateStore(notificationsMuted);
        try {
          await queryFulfilled;
          const action = notificationsMuted ? 'muted' : 'unmuted';
          dispatch(
            alertSnackbar({
              successMessage: `Email notifications ${action} for ${clientName}`,
            }),
          );
        } catch (err) {
          // Rollback on failure
          updateStore(!notificationsMuted);
          const { message } = ensureApiError(err);
          dispatch(alertSnackbar({ errorMessage: message }));
        }
      },
    }),
  }),
});

export const {
  useInviteClientMutation,
  useUpdateMfaMutation,
  useUpdateNotificationsMutedMutation,
} = clientsApi;
